import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaUniversity } from '@react-icons/all-files/fa/FaUniversity';
import { FaBuilding } from '@react-icons/all-files/fa/FaBuilding';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import Breadcrumb from '../../../commons/Breadcrumb';
import PageHeader from '../../../commons/PageHeader';
import PageForm, { FormField } from '../../../commons/PageForm';
import PageAlert, { usePageAlert } from '../../../commons/PageAlert';
import { useAppDispatch, useAppSelector } from '../../../store';
import { getEntity, createEntity, updateEntity, reset } from '../../../manager/regimen.reducer';
import { EMPRESA_CONFIG_BASE, REGIMEN_BASE } from './empresa-config-routes';
import { RegimenFormValues, toRegimenPayload, validateRegimenForm } from './regimen-form';

const empty: RegimenFormValues = {
  id: '',
  codigo: '',
  descripcion: '',
  valor: '',
};

const fields: FormField<RegimenFormValues>[] = [
  { name: 'id', label: 'ID', readOnly: true, editOnly: true },
  { name: 'codigo', label: 'Código', type: 'number', required: true, placeholder: 'Ej: 601' },
  { name: 'descripcion', label: 'Descripción', type: 'textarea', rows: 3, required: true, placeholder: 'Descripción del régimen' },
  { name: 'valor', label: 'Valor', required: true, placeholder: 'Valor del régimen' },
];

export default function RegimenFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const entity = useAppSelector(s => s.regimen.entity) as any;
  const updating = useAppSelector(s => s.regimen.updating);
  const updateSuccess = useAppSelector(s => s.regimen.updateSuccess);
  const errorMessage = useAppSelector(s => s.regimen.errorMessage) as string | null;

  const [form, setForm] = useState<RegimenFormValues>(empty);
  const [errors, setErrors] = useState<Partial<Record<keyof RegimenFormValues, string>>>({});
  const { alert, showToast } = usePageAlert();

  useEffect(() => {
    if (!isNew) {
      dispatch(getEntity(id!));
    } else {
      dispatch(reset());
      setForm(empty);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isNew && entity?.id) {
      setForm({
        id: String(entity.id),
        codigo: entity.codigo == null ? '' : String(entity.codigo),
        descripcion: entity.descripcion ?? '',
        valor: entity.valor ?? '',
      });
    }
  }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      navigate(REGIMEN_BASE, {
        state: {
          successMsg: isNew
            ? 'Régimen creado correctamente.'
            : 'Régimen actualizado correctamente.',
        },
      });
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (errorMessage) showToast('error', errorMessage);
  }, [errorMessage, showToast]);

  const handleFieldChange = (name: keyof RegimenFormValues, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validateRegimenForm(form);
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const payload = toRegimenPayload(form);
    if (isNew) {
      dispatch(createEntity(payload));
    } else {
      dispatch(updateEntity(payload));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Empresa', to: EMPRESA_CONFIG_BASE, icon: <FaBuilding /> },
        { label: 'Régimen', to: REGIMEN_BASE, icon: <FaUniversity /> },
        { label: isNew ? 'Nuevo régimen' : `Editar régimen #${id}` },
      ]} />

      <PageHeader
        icon={<FaUniversity />}
        iconColor="#6366f1"
        title={isNew ? 'Nuevo régimen' : 'Editar régimen'}
        subtitle={isNew
          ? 'Completa los campos para crear un régimen'
          : `Modificando régimen #${id}`}
      />

      {alert && <PageAlert alert={alert} />}

      <PageForm
        fields={fields}
        values={form}
        errors={errors}
        isNew={isNew}
        saving={updating}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={() => navigate(REGIMEN_BASE)}
        accentColor="#6366f1"
      />
    </div>
  );
}
